import { LitElement, html } from 'lit';
// eslint-disable-next-line
import styles from '!!lit-css-loader!postcss-loader!sass-loader!./MediaViewer.scss';

class InstagramVideo extends LitElement {
  static styles = [styles];

  static get properties() {
    return {
      src: { type: String },
      muted: { type: Boolean },
      controls: { type: Boolean },
      playing: { type: Boolean },
      onVideoError: { type: Function },
    };
  }

  constructor() {
    super();
    this.isPlaying = false;
    this.player = null;
    this.hasError = false;
  }

  /**
   * Handles video errors including CORS issues with Instagram videos
   */
  handleVideoError = () => {
    // Critical point: Check if onVideoError callback exists before calling
    if (this.onVideoError && typeof this.onVideoError === 'function') {
      this.onVideoError({ target: this.player });
    } else {
      // Fallback: Mark component as having error for graceful degradation
      this.hasError = true;
      this.requestUpdate();
    }
  };

  firstUpdated() {
    this.player = this.shadowRoot.querySelector('#video-player');

    if (this.player) {
      // Critical point: Add comprehensive error handling for CORS and loading issues
      this.player.addEventListener('error', this.handleVideoError);
      this.player.addEventListener('loadstart', () => {
        this.hasError = false;
      });

      if (this.playing && !this.hasError) {
        this.player.muted = this.muted;
        this.player.play().catch((err) => {
          // Handle play errors (autoplay restrictions, CORS, etc.)
          this.handleVideoError(err);
        });
        this.isPlaying = true;
      }
    }
  }

  updated(changedProperties) {
    super.updated();
    const { playing } = this;

    if (changedProperties.has('playing') && this.player && !this.hasError) {
      if (playing && !this.isPlaying) {
        this.player.play().catch((err) => {
          this.handleVideoError(err);
        });
        this.isPlaying = true;
      } else if (!playing && this.isPlaying) {
        this.player.pause();
        this.isPlaying = false;
      }
    }
  }

  /**
   * Cleanup when component is disconnected
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.player) {
      this.player.removeEventListener('error', this.handleVideoError);
    }
  }

  render() {
    const { src, muted = false, controls } = this;

    // If there's an error, don't render the video element
    if (this.hasError) {
      return html`<div class="media-viewer__video-error">Video unavailable</div>`;
    }

    return muted
      ? html`
          <video
            id="video-player"
            src=${src}
            class="media-viewer__video"
            type="application/x-mpegURL"
            autoplay
            oncanplay="this.muted = ${+muted}"
            loop
            ?controls=${controls}
            preload="auto"
            playsinline
            crossorigin="anonymous"
          ></video>
        `
      : html`
          <video
            id="video-player"
            src=${src}
            class="media-viewer__video"
            type="application/x-mpegURL"
            ?controls=${controls}
            preload="auto"
            playsinline
            crossorigin="anonymous"
          ></video>
        `;
  }
}

export default InstagramVideo;
